/**
 * Bloque ```json-chart ... ``` (gráficos Recharts).
 * El modelo a menudo devuelve JSON casi válido: lo normalizamos antes de JSON.parse.
 */

export function indexOfJsonChartFenceStart(text) {
  if (!text || typeof text !== 'string') return -1;
  const m = text.match(/```\s*json-chart/i);
  return m && m.index !== undefined ? m.index : -1;
}

/**
 * Interior del bloque: desde ```json-chart hasta el último ``` (cierre del fence).
 */
export function extractJsonChartInnerIfClosed(text) {
  const openIdx = indexOfJsonChartFenceStart(text);
  if (openIdx === -1) return { complete: false, inner: null };
  const fromOpen = text.slice(openIdx);
  const mOpen = fromOpen.match(/^```\s*json-chart\s*\n?/i);
  if (!mOpen) return { complete: false, inner: null };
  const rest = fromOpen.slice(mOpen[0].length);
  const closeIdx = rest.lastIndexOf('```');
  if (closeIdx === -1) return { complete: false, inner: null };
  return { complete: true, inner: rest.slice(0, closeIdx) };
}

function stripTrailingCommas(jsonLike) {
  let t = jsonLike;
  let prev;
  do {
    prev = t;
    t = t.replace(/,(\s*[\]}])/g, '$1');
  } while (t !== prev);
  return t;
}

function fixPythonLiterals(s) {
  return s
    .replace(/\bTrue\b/g, 'true')
    .replace(/\bFalse\b/g, 'false')
    .replace(/\bNone\b/g, 'null');
}

/** Claves sin comillas: { foo: → { "foo": (solo fuera de strings; heurística). */
function quoteUnquotedKeys(s) {
  return s.replace(/([{,]\s*)([a-zA-Z_][a-zA-Z0-9_]*)\s*:/g, '$1"$2":');
}

function fixEuropeanNumberTokens(s) {
  let t = s.replace(/:\s*(-?\d+),(\d+)\b/g, ': $1.$2');
  t = t.replace(/,\s*(-?\d+),(\d+)\b/g, ', $1.$2');
  t = t.replace(/\[\s*(-?\d+),(\d+)\b/g, '[$1.$2');
  return t;
}

/** Primer objeto {…} balanceado (strings con " y escapes). */
function extractBalancedJsonObject(str) {
  const start = str.indexOf('{');
  if (start === -1) return null;
  let depth = 0;
  let inString = false;
  let escape = false;
  for (let i = start; i < str.length; i++) {
    const c = str[i];
    if (!inString) {
      if (c === '"') {
        inString = true;
        continue;
      }
      if (c === '{') depth += 1;
      else if (c === '}') {
        depth -= 1;
        if (depth === 0) return str.slice(start, i + 1);
      }
      continue;
    }
    if (escape) {
      escape = false;
      continue;
    }
    if (c === '\\') {
      escape = true;
      continue;
    }
    if (c === '"') inString = false;
  }
  return null;
}

/** Todos los substrings {…} balanceados (por si hay basura alrededor). */
function extractAllBalancedObjects(str) {
  const out = [];
  for (let i = 0; i < str.length; i++) {
    if (str[i] !== '{') continue;
    const one = extractBalancedJsonObject(str.slice(i));
    if (one) out.push(one);
  }
  return out;
}

function parseNumberLoose(v) {
  if (typeof v === 'number' && Number.isFinite(v)) return v;
  if (v == null) return 0;
  if (typeof v !== 'string') {
    const n = Number(v);
    return Number.isFinite(n) ? n : 0;
  }
  const t = v.trim().replace(/\s/g, '').replace(/€/g, '');
  if (/^-?\d+,\d+$/.test(t)) return parseFloat(t.replace(',', '.'));
  if (/^-?\d+\.\d+$/.test(t)) return parseFloat(t);
  if (/^-?\d+$/.test(t)) return parseFloat(t);
  const european = t.replace(/\./g, '').replace(',', '.');
  const n2 = parseFloat(european);
  return Number.isFinite(n2) ? n2 : 0;
}

function normalizeRows(rows) {
  if (!Array.isArray(rows)) return [];
  return rows.map((item) => {
    if (item == null || typeof item !== 'object') {
      return { label: String(item ?? ''), value: parseNumberLoose(item) };
    }
    const label =
      item.label ??
      item.name ??
      item.x ??
      item.fecha ??
      item.day ??
      item.categoria ??
      item.periodo ??
      item.date ??
      '';
    const rawVal =
      item.value ??
      item.y ??
      item.importe ??
      item.amount ??
      item.total ??
      item.ventas ??
      item.cantidad ??
      item.sum ??
      0;
    return { label: String(label), value: parseNumberLoose(rawVal) };
  });
}

/**
 * Acepta data / datos / points / items, pares labels+values, o raíz array.
 */
export function coerceChartPayload(raw) {
  if (raw == null) return null;
  if (Array.isArray(raw)) {
    if (raw.length === 0) return { type: 'bar', title: '', data: [] };
    if (typeof raw[0] === 'object') {
      return { type: 'bar', title: '', data: normalizeRows(raw) };
    }
    return null;
  }
  if (typeof raw !== 'object') return null;

  let rows = null;
  const arrayKeys = ['data', 'datos', 'points', 'items', 'rows', 'values', 'barras'];
  for (const k of arrayKeys) {
    if (k === 'values' && Array.isArray(raw.values) && Array.isArray(raw.labels)) {
      rows = raw.labels.map((label, i) => ({
        label: String(label ?? ''),
        value: parseNumberLoose(raw.values[i]),
      }));
      break;
    }
    if (Array.isArray(raw[k]) && k !== 'values') {
      rows = raw[k];
      break;
    }
  }
  if (rows === null && Array.isArray(raw.series) && Array.isArray(raw.categories)) {
    rows = raw.categories.map((label, i) => ({
      label: String(label ?? ''),
      value: parseNumberLoose(raw.series[i]),
    }));
  }
  if (!Array.isArray(rows)) return null;

  const title = raw.title ?? raw.titulo ?? '';
  return {
    type: typeof raw.type === 'string' ? raw.type : 'bar',
    title: typeof title === 'string' ? title : String(title ?? ''),
    data: normalizeRows(rows),
  };
}

function tryJsonParseCoerce(text) {
  try {
    const raw = JSON.parse(text);
    const coerced = coerceChartPayload(raw);
    if (coerced && Array.isArray(coerced.data)) return coerced;
  } catch {
    /* siguiente */
  }
  return null;
}

function preprocessJsonLike(s) {
  let t = s.trim();
  t = t.replace(/^```(?:json)?\s*\n?/i, '').replace(/\n?```\s*$/i, '').trim();
  t = fixPythonLiterals(t);
  t = fixEuropeanNumberTokens(t);
  return t;
}

/**
 * Intenta parsear candidatos (texto completo, objetos balanceados, trozos entre ```).
 */
export function parseJsonChartInner(innerRaw) {
  if (!innerRaw || typeof innerRaw !== 'string') return null;

  const segments = innerRaw.split(/```+/).map((p) => p.trim()).filter(Boolean);

  const attemptOne = (chunk) => {
    let s = preprocessJsonLike(chunk);
    const variants = [
      s,
      stripTrailingCommas(s),
      quoteUnquotedKeys(s),
      stripTrailingCommas(quoteUnquotedKeys(s)),
    ];
    for (const v of variants) {
      const ok = tryJsonParseCoerce(v);
      if (ok) return ok;
    }
    const balanced = extractBalancedJsonObject(s);
    if (balanced) {
      for (const v of [balanced, stripTrailingCommas(balanced), quoteUnquotedKeys(balanced)]) {
        const ok = tryJsonParseCoerce(v);
        if (ok) return ok;
      }
    }
    for (const obj of extractAllBalancedObjects(s)) {
      for (const v of [obj, stripTrailingCommas(obj), quoteUnquotedKeys(obj)]) {
        const ok = tryJsonParseCoerce(v);
        if (ok) return ok;
      }
    }
    return null;
  };

  for (const seg of segments) {
    const r = attemptOne(seg);
    if (r) return r;
  }
  return attemptOne(innerRaw);
}

const _fenceWithValidChartData = (text) => {
  const { complete, inner } = extractJsonChartInnerIfClosed(text);
  if (!complete || inner == null) return { ok: false, dataLen: -1 };
  const o = parseJsonChartInner(inner);
  if (!o) return { ok: false, dataLen: -1 };
  return { ok: true, dataLen: o.data.length };
};

export function hasCompleteJsonChartBlock(text) {
  return extractJsonChartInnerIfClosed(text).complete;
}

export function mergeAssistantStreamWithFinal(streamedAccumulated, serverFinal) {
  const s = typeof serverFinal === 'string' ? serverFinal : '';
  const st = typeof streamedAccumulated === 'string' ? streamedAccumulated : '';
  const vs = _fenceWithValidChartData(s);
  const vst = _fenceWithValidChartData(st);
  if (vst.ok && !vs.ok) return st;
  if (vs.ok && !vst.ok) return s;
  if (vs.ok && vst.ok) {
    if (vst.dataLen > vs.dataLen) return st;
    if (vs.dataLen > vst.dataLen) return s;
    return s.trim().length >= st.trim().length ? s : st;
  }
  if (hasCompleteJsonChartBlock(st) && !hasCompleteJsonChartBlock(s)) return st;
  if (hasCompleteJsonChartBlock(s) && !hasCompleteJsonChartBlock(st)) return s;
  if (s.trim().length >= st.trim().length && s.trim()) return s;
  return st || s;
}

export function getAssistantChartRenderState(content) {
  if (!content || typeof content !== 'string') {
    return { phase: 'plain', prefix: '' };
  }
  const openIdx = indexOfJsonChartFenceStart(content);
  if (openIdx === -1) {
    return { phase: 'plain', prefix: content };
  }
  const { complete, inner } = extractJsonChartInnerIfClosed(content);
  if (!complete) {
    return {
      phase: 'streaming',
      prefix: content.slice(0, openIdx).trimEnd(),
    };
  }
  const chartData = parseJsonChartInner(inner);
  return {
    phase: 'complete',
    prefix: content.slice(0, openIdx).trimEnd(),
    chartData,
  };
}

export function splitMessageForChartExport(content) {
  if (!content || typeof content !== 'string') {
    return { hasChart: false, textPart: content || '' };
  }
  const openIdx = indexOfJsonChartFenceStart(content);
  if (openIdx === -1) return { hasChart: false, textPart: content };
  const { complete, inner } = extractJsonChartInnerIfClosed(content);
  const textPart = content.slice(0, openIdx).trim();
  if (!complete) return { hasChart: false, textPart: content };
  const chartData = parseJsonChartInner(inner);
  return { hasChart: true, textPart, chartData };
}
